import React from 'react';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import InsightColumnPair from '../InsightColumnPair';
import '../../styles/insightCards.css';

interface BaitPricesInsights {
  avgHerringPrice?: number;
  avgMenhadenPrice?: number;
  avgWeightedBaitPrice?: number;
}

interface EndogenousBaitMarketPricesData {
  insights?: BaitPricesInsights | null;
  originalInsights?: BaitPricesInsights | null;
  showStatusQuoColumn?: boolean;
  showSingleColumn?: boolean;
  showChangeIndicators?: boolean;
  onOpenHerringBaitPriceModal?: () => void;
}

export default function EndogenousBaitMarketPrices(
  props: RegistryComponentProps
): React.ReactElement {
  const pageData = usePageDataContextOptional();
  const data = getRegistryData<EndogenousBaitMarketPricesData>(props);
  const pageInsights = pageData?.data?.insights as BaitPricesInsights | null | undefined;
  const pageOriginalInsights = pageData?.data?.originalInsights as BaitPricesInsights | null | undefined;
  const pageOpenHerringBaitPriceModal = pageData?.actions?.openHerringBaitPriceModal as (() => void) | undefined;
  const insights = data.insights ?? pageInsights;
  const originalInsights = data.originalInsights ?? pageOriginalInsights;
  const showStatusQuoColumn = data.showStatusQuoColumn ?? true;
  const showSingleColumn = data.showSingleColumn ?? false;
  const showChangeIndicators = data.showChangeIndicators ?? false;
  const onOpenHerringBaitPriceModal = data.onOpenHerringBaitPriceModal ?? pageOpenHerringBaitPriceModal;
  const statusQuoDisplay = (originalInsights ?? insights) as BaitPricesInsights;

  if (!insights) {
    return (
      <div className="insight-card">
        <div className="insight-section-header insight-section-header--first">
          <h4 className="insight-section-header__title">Endogenous Bait Market Prices</h4>
        </div>
        <div className="loading-message" style={{ padding: '20px', textAlign: 'center' }}>
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="insight-card">
      <div className="insight-section-header insight-section-header--first">
        <h4 className="insight-section-header__title">Endogenous Bait Market Prices</h4>
      </div>

      <InsightColumnPair
        originalValue={originalInsights?.avgHerringPrice}
        currentValue={insights.avgHerringPrice}
        statusQuoContent={
          <>${(statusQuoDisplay.avgHerringPrice ?? 0).toFixed(3)}/lb</>
        }
        currentContent={<>${(insights.avgHerringPrice ?? 0).toFixed(3)}/lb</>}
        showStatusQuoColumn={showStatusQuoColumn}
        showSingleColumn={showSingleColumn}
        showChangeIndicators={Boolean(showChangeIndicators)}
        parametersChanged={false}
        onClick={onOpenHerringBaitPriceModal}
        clickable={Boolean(onOpenHerringBaitPriceModal)}
        statusQuoHeader="Herring Price (Status Quo)"
        currentHeader={showStatusQuoColumn ? 'Herring Price (Adjusted)' : ''}
      />

      <InsightColumnPair
        originalValue={originalInsights?.avgMenhadenPrice}
        currentValue={insights.avgMenhadenPrice}
        statusQuoContent={
          <>${(statusQuoDisplay.avgMenhadenPrice ?? 0).toFixed(3)}/lb</>
        }
        currentContent={<>${(insights.avgMenhadenPrice ?? 0).toFixed(3)}/lb</>}
        showStatusQuoColumn={showStatusQuoColumn}
        showSingleColumn={showSingleColumn}
        showChangeIndicators={Boolean(showChangeIndicators)}
        parametersChanged={false}
        statusQuoHeader="Menhaden Price (Status Quo)"
        currentHeader={showStatusQuoColumn ? 'Menhaden Price (Adjusted)' : ''}
        wrapperStyle={{ marginTop: '10px' }}
      />

      <InsightColumnPair
        originalValue={originalInsights?.avgWeightedBaitPrice}
        currentValue={insights.avgWeightedBaitPrice}
        statusQuoContent={
          <>${(statusQuoDisplay.avgWeightedBaitPrice ?? 0).toFixed(3)}/lb</>
        }
        currentContent={<>${(insights.avgWeightedBaitPrice ?? 0).toFixed(3)}/lb</>}
        showStatusQuoColumn={showStatusQuoColumn}
        showSingleColumn={showSingleColumn}
        showChangeIndicators={Boolean(showChangeIndicators)}
        parametersChanged={false}
        statusQuoHeader="Weighted Avg Bait Price (Status Quo)"
        currentHeader={showStatusQuoColumn ? 'Weighted Avg Bait Price (Adjusted)' : ''}
        wrapperStyle={{ marginTop: '10px' }}
      />
    </div>
  );
}
